/**
 * Private Chat Modal Component
 * Direct messaging interface between players
 */
import React from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X } from 'lucide-react';

const PrivateChatModal = ({
  isOpen,
  onClose,
  targetPlayer,
  privateMessages,
  setPrivateMessages,
  socketRef
}) => {
  if (!isOpen || !targetPlayer) return null;

  const filteredMessages = privateMessages.filter(
    m => m.from === targetPlayer.id || m.to === targetPlayer.id
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const input = e.target.elements.privateMsg;
    if (input.value.trim() && socketRef.current?.connected) {
      socketRef.current.emit('private_message', {
        to: targetPlayer.id,
        message: input.value.trim()
      });
      setPrivateMessages(prev => [...prev, {
        to: targetPlayer.id,
        text: input.value.trim(),
        timestamp: Date.now(),
        isIncoming: false,
      }]);
      input.value = '';
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9990] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
      data-testid="private-chat-overlay"
    >
      <Card 
        className="w-full sm:max-w-md bg-gray-900 border-purple-500/30 rounded-t-2xl sm:rounded-2xl max-h-[70vh] flex flex-col"
        data-testid="private-chat-modal"
      >
        <CardHeader className="flex flex-row items-center justify-between py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-500/30 flex items-center justify-center">
              💬
            </div>
            <div>
              <p className="text-white font-medium">{targetPlayer.name}</p>
              <p className="text-white/40 text-xs">Private chat</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-private-chat">
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[200px]">
          {filteredMessages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.isIncoming ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${msg.isIncoming ? 'bg-white/10' : 'bg-purple-600'}`}>
                <p className="text-white text-sm">{msg.text}</p>
              </div>
            </div>
          ))}
          {filteredMessages.length === 0 && (
            <p className="text-white/40 text-center text-sm py-8">Start a conversation!</p>
          )}
        </CardContent>
        <div className="p-3 border-t border-white/10">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input 
              name="privateMsg"
              placeholder="Type a message..."
              className="flex-1 bg-black/30 border-white/20 text-white"
              data-testid="private-chat-input"
            />
            <Button type="submit" className="bg-purple-600 hover:bg-purple-500" data-testid="send-private-msg">
              Send
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default PrivateChatModal;
